import CryptoJS from 'crypto-js';

/**
 * `.xts` 导出包的加解密。
 *
 * ## 为什么改
 * 旧实现直接 `CryptoJS.AES.encrypt(json, password)`，那是 OpenSSL 兼容信封：
 * - 密钥派生是 **单轮 MD5**（`EVP_BytesToKey`），弱口令可在 GPU 上离线爆破；
 * - **无任何认证**（CBC + PKCS#7），密文可被篡改而不被发现，也无法区分
 *   "密码错误"与"文件损坏"——两种故障都只提示"密码错误"，把用户引向反复试密码。
 *
 * ## 新格式（v2）
 * ```
 * XTS1|v2|PBKDF2-SHA256|600000|AES-GCM-256|<base64 salt>|<base64 iv>|<base64 ciphertext+tag>
 * ```
 * - 明文头可在解密前识别格式/版本，并对头部做 AAD 绑定（改头即解密失败）；
 * - PBKDF2-SHA256 600k 次迭代派生密钥，AES-256-GCM 提供机密性与完整性；
 * - 密码错误与文件被篡改会得到**可区分的错误码**。
 *
 * v1（旧 CryptoJS 信封）仍然可以解密，保证历史导出文件不失效。
 */

const MAGIC = 'XTS1';
const V2_KDF = 'PBKDF2-SHA256';
const V2_ITERATIONS = 600_000;
const V2_ALGO = 'AES-GCM-256';

/** v2 头部固定前缀，同时作为 AEAD 的 additionalData（头部被篡改则认证失败） */
function v2Header(): string {
  return `${MAGIC}|v2|${V2_KDF}|${V2_ITERATIONS}|${V2_ALGO}`;
}

/**
 * 备份包最少密码长度。
 *
 * 刻意只做**长度下限、不做强度要求**：这是单用户个人写作工具的本地备份，
 * 不是对外服务 —— 强制大小写/符号混合只会让用户把密码写在便签上，
 * 反而降低实际安全性。AES-GCM + PBKDF2 600k 迭代本身已提供足够保护。
 */
export const MIN_EXPORT_PASSWORD_LENGTH = 4;

/** 当前导出格式版本 */
export const EXPORT_VERSION = '1.0';
const EXPORT_TYPE = 'xiaotianshu_export';

export interface ExportData {
  version: string;
  type: string;
  timestamp: string;
  workInfo: unknown;
  volumes: unknown[];
  chapters: unknown[];
  chapterVersions?: unknown[];
  settings: unknown[];
  factions: unknown[];
  characters: unknown[];
  items: unknown[];
  outline: unknown;
}

/** 解密结果：区分失败原因，让 UI 能给出正确指引 */
export type DecryptResult =
  | { ok: true; data: ExportData }
  | { ok: false; reason: 'wrong-password' | 'corrupted' | 'unsupported-version' | 'not-this-app' | 'invalid-password'; message: string };

// ── 工具 ─────────────────────────────────────────────────────────────

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function fromBase64(b64: string): Uint8Array {
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** 备份密码校验：只要求达到最小长度 */
export function checkExportPassword(password: string): string | null {
  if (!password || password.length < MIN_EXPORT_PASSWORD_LENGTH) {
    return `备份密码至少 ${MIN_EXPORT_PASSWORD_LENGTH} 位`;
  }
  return null;
}

async function deriveKey(password: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ── 加密 ─────────────────────────────────────────────────────────────

/**
 * 加密导出数据（v2 格式）。密码强度不合格时抛错——调用方应先经 checkExportPassword。
 */
export async function encryptData(data: ExportData, password: string): Promise<string> {
  const weak = checkExportPassword(password);
  if (weak) throw new Error(weak);

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const header = v2Header();
  const key = await deriveKey(password, salt, V2_ITERATIONS);

  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as unknown as BufferSource, additionalData: new TextEncoder().encode(header) },
    key,
    plaintext,
  ));

  return `${header}|${toBase64(salt)}|${toBase64(iv)}|${toBase64(ciphertext)}`;
}

// ── 解密 ─────────────────────────────────────────────────────────────

/** v1（旧 CryptoJS 信封）兼容解密 */
function decryptV1(encryptedStr: string, password: string): DecryptResult {
  let decrypted = '';
  try {
    decrypted = CryptoJS.AES.decrypt(encryptedStr, password).toString(CryptoJS.enc.Utf8);
  } catch {
    // CBC 的 PKCS#7 校验失败会抛错 —— 绝大多数情况是口令不对
    return { ok: false, reason: 'wrong-password', message: '密码错误' };
  }
  if (!decrypted) return { ok: false, reason: 'wrong-password', message: '密码错误' };
  // 能解出 UTF-8 文本再解析 JSON，才说明口令正确
  try {
    const data = JSON.parse(decrypted) as ExportData;
    return shapeCheck(data);
  } catch {
    return { ok: false, reason: 'wrong-password', message: '密码错误' };
  }
}

/** 校验载荷形状（新旧格式共用） */
function shapeCheck(data: ExportData): DecryptResult {
  const error = validateExportShape(data);
  if (error) return { ok: false, reason: 'not-this-app', message: error };
  return { ok: true, data };
}

/** 解密 v2 格式 */
async function decryptV2(encryptedStr: string, password: string): Promise<DecryptResult> {
  const parts = encryptedStr.split('|');
  if (parts.length !== 8) {
    return { ok: false, reason: 'corrupted', message: '文件已损坏（头部字段缺失）' };
  }
  const [magic, ver, kdf, iterStr, algo, saltB64, ivB64, ctB64] = parts;
  if (magic !== MAGIC) {
    return { ok: false, reason: 'not-this-app', message: '这不是小天书的导出文件' };
  }
  if (ver !== 'v2') {
    return { ok: false, reason: 'unsupported-version', message: `不支持的导出格式版本：${ver}（请升级应用）` };
  }
  const iterations = Number(iterStr);
  if (kdf !== V2_KDF || algo !== V2_ALGO || !Number.isFinite(iterations) || iterations <= 0) {
    return { ok: false, reason: 'unsupported-version', message: '不支持的加密参数（请升级应用）' };
  }

  const header = `${magic}|${ver}|${kdf}|${iterStr}|${algo}`;
  try {
    const salt = fromBase64(saltB64);
    const iv = fromBase64(ivB64);
    const ciphertext = fromBase64(ctB64);
    const key = await deriveKey(password, salt, iterations);
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource, additionalData: new TextEncoder().encode(header) },
      key,
      ciphertext as unknown as BufferSource,
    );
    const data = JSON.parse(new TextDecoder().decode(plaintext)) as ExportData;
    return shapeCheck(data);
  } catch (e) {
    // GCM 认证失败：口令错误或密文被篡改在密码学上不可区分。
    // 但头部是明文的，字段缺失已在上方归类为 corrupted，故此处按口令错误处理。
    if (e instanceof SyntaxError) {
      return { ok: false, reason: 'corrupted', message: '文件已损坏（内容无法解析）' };
    }
    return { ok: false, reason: 'wrong-password', message: '密码错误' };
  }
}

/**
 * 解密导出数据。v2 走 WebCrypto，v1（旧 `Salted__` 信封）回退到 CryptoJS。
 */
export async function decryptData(encryptedStr: string, password: string): Promise<DecryptResult> {
  // 同样剥离 BOM：否则带 BOM 的 v2 文件不会命中 `XTS1|` 前缀，
  // 会被误判为 v1 并给出"密码错误"这一误导性结论。
  const trimmed = (encryptedStr ?? '').replace(/^\uFEFF/, '').trim();
  if (!trimmed) {
    return { ok: false, reason: 'corrupted', message: '文件内容为空' };
  }
  if (!password) {
    return { ok: false, reason: 'invalid-password', message: '请输入导出密码' };
  }
  if (trimmed.startsWith(`${MAGIC}|`)) {
    return decryptV2(trimmed, password);
  }
  // 旧格式：CryptoJS 的 OpenSSL 信封（base64 的 "Salted__…"）
  return decryptV1(trimmed, password);
}

/**
 * 创建导出数据包（保留旧签名以兼容调用方）。
 */
export function createExportData(
  workInfo: unknown,
  volumes: unknown[],
  chapters: unknown[],
  settings: unknown[],
  factions: unknown[],
  characters: unknown[],
  items: unknown[],
  outline: unknown,
): ExportData {
  return {
    version: EXPORT_VERSION,
    type: EXPORT_TYPE,
    timestamp: new Date().toISOString(),
    workInfo, volumes, chapters, settings, factions, characters, items, outline,
  };
}

/**
 * 仅检查明文头部即可判定"是不是本应用的 v2 文件"，无需先解密。
 * v1 文件无明文头，返回 'v1' 由调用方按旧路径处理。
 */
export function detectFormat(content: string): 'v2' | 'v1' | 'unknown' {
  const trimmed = (content ?? '').replace(/^\uFEFF/, '').trim();
  if (trimmed.startsWith(`${MAGIC}|`)) return 'v2';
  if (/^U2FsdGVkX1/.test(trimmed)) return 'v1';
  return 'unknown';
}

/** 载荷形状校验（不涉及口令）：返回错误文案，合法则返回 null */
function validateExportShape(data: unknown): string | null {
  if (!data || typeof data !== 'object') {
    return '文件内容不是小天书导出数据';
  }
  const type = (data as { type?: unknown }).type;
  if (type !== EXPORT_TYPE && type !== 'xiaotianshu_batch_export') {
    return '这不是小天书的导出文件';
  }
  return null;
}

/**
 * 是否需要口令才能读取这个文件。
 *
 * 为什么需要：导入流程此前**无条件**弹出密码框 —— 拿到文件就直接问密码，
 * 从不判断文件究竟是不是加密的。于是「导出时没设过密码」的明文文件
 * 也会被要求输入密码，而那个密码根本不存在，用户只能反复试。
 *
 * 返回三类：
 *   - `encrypted`：是 v1/v2 加密包，需要口令
 *   - `plaintext`：是未加密的导出 JSON（可直接导入；历史上存在这种包）
 *   - `not-an-export`：既非加密包也不是本应用的导出数据，附原因
 */
export type ImportProbe =
  | { kind: 'encrypted'; format: 'v1' | 'v2' }
  | { kind: 'plaintext'; data: ExportData }
  | { kind: 'not-an-export'; message: string };

export function probeImportContent(content: string): ImportProbe {
  // 剥离 UTF-8 BOM：本应用的文本导出都带 BOM，用户若用编辑器重新保存
  // 明文导出包就可能带上 BOM，而 BOM 会让 JSON.parse 直接失败。
  const trimmed = (content ?? '').replace(/^\uFEFF/, '').trim();
  if (!trimmed) {
    return { kind: 'not-an-export', message: '文件内容为空' };
  }

  const format = detectFormat(trimmed);
  if (format !== 'unknown') {
    return { kind: 'encrypted', format };
  }

  // 未加密：尝试按导出 JSON 解析（旧版本或手工导出的明文包）
  try {
    const parsed: unknown = JSON.parse(trimmed);
    const shapeError = validateExportShape(parsed);
    if (shapeError) return { kind: 'not-an-export', message: shapeError };
    return { kind: 'plaintext', data: parsed as ExportData };
  } catch {
    return {
      kind: 'not-an-export',
      message: '无法识别该文件：既不是小天书加密导出包（.xts），也不是其明文导出数据',
    };
  }
}
